import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonStandard,
  ButtonStandardHoverEffectType,
  ButtonStandardType
} from '../../button/ButtonStandard/ButtonStandard';
import { CardBody } from '../../card/CardBody/CardBody';
import {
  CardHeader,
  HorizontalAlignment
} from '../../card/CardHeader/CardHeader';
import {
  CardStandard,
  CardStandardProps
} from '../../card/CardStandard/CardStandard';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import { TextInput, TextInputType } from '../../input/TextInput/TextInput';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { FormGroup } from '../FormGroup/FormGroup';
import styles from './ForgotPasswordForm.module.css';

export type ForgotPasswordFormProps =
  PropsWithoutChildren<CardStandardProps> & {
    sendPasswortResetMail: (email: string) => Promise<void>;
    onCancel: () => void;
    defaultEmail?: string;
  };

export const ForgotPasswordForm: FunctionComponent<ForgotPasswordFormProps> =
  React.memo((props) => {
    const {
      className,
      defaultEmail,
      sendPasswortResetMail,
      onCancel,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const [email, setEmail] = useState(defaultEmail ?? '');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [sendingEmailFailed, setSendingEmailFailed] = useState(false);
    const [noEmailAddressEntered, setNoEmailAddressEntered] = useState(false);

    const { t } = useTranslation('record-it-components');
    const notificationManager = useNotificationManager();

    const handleSendEmailButtonClick = useEventCallback(() => {
      if (isSendingEmail) {
        return;
      }

      const sanitizedEmail = email.trim();
      if (sanitizedEmail === '') {
        setNoEmailAddressEntered(true);
        return;
      }

      setNoEmailAddressEntered(false);
      setIsSendingEmail(true);
      setSendingEmailFailed(false);

      void sendPasswortResetMail(email)
        .then(() => {
          notificationManager.success({
            title: t('form.ForgotPasswordForm.successNotification.title'),
            message: t('form.ForgotPasswordForm.successNotification.message')
          });
        })
        .catch((e) => {
          console.error(e);
          setSendingEmailFailed(true);
        })
        .finally(() => {
          setIsSendingEmail(false);
        });
    });

    return (
      <CardStandard
        {...remainingProps}
        className={classes(className, styles.card)}
      >
        <CardHeader textAlignment={HorizontalAlignment.Center}>
          {t('form.ForgotPasswordForm.title')}
        </CardHeader>

        <CardBody>
          {sendingEmailFailed ? (
            <MessageBox
              type={MessageBoxType.Error}
              className={styles.sendingEmailErrorMessageBox}
              data-testid="ForgotPasswordForm.SendingEmailFailedText"
            >
              {t('form.ForgotPasswordForm.sendingEmailFailedText')}
            </MessageBox>
          ) : null}

          {noEmailAddressEntered ? (
            <MessageBox
              type={MessageBoxType.Error}
              className={styles.sendingEmailErrorMessageBox}
              data-testid="ForgotPasswordForm.NoEmailAddressEnteredText"
            >
              {t('form.ForgotPasswordForm.noEmailAddressEnteredText')}
            </MessageBox>
          ) : null}

          <MessageBox
            type={MessageBoxType.Info}
            className={styles.infoMessageBox}
          >
            {t('form.ForgotPasswordForm.infoText')}
          </MessageBox>

          <FormGroup>
            <TextInput
              label={t('form.ForgotPasswordForm.emailLabel')}
              type={TextInputType.Email}
              value={email}
              onInput={setEmail}
              data-testid="ForgotPasswordForm.EmailTextInput"
            ></TextInput>
          </FormGroup>

          <div className={styles.sendEmailButtonWrapper}>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={handleSendEmailButtonClick}
              data-testid="ForgotPasswordForm.SendEmailButton"
            >
              {t('form.ForgotPasswordForm.sendEmail')}
            </ButtonStandard>
          </div>

          <div className={styles.cancelButtonWrapper}>
            <ButtonStandard
              type={ButtonStandardType.TextSecondary}
              hoverEffectType={ButtonStandardHoverEffectType.HalfTransparent}
              onClick={onCancel}
              data-testid="ForgotPasswordForm.CancelButton"
            >
              {t('form.ForgotPasswordForm.cancel')}
            </ButtonStandard>
          </div>

          <LoadingOverlay
            active={isSendingEmail}
            data-testid="ForgotPasswordForm.LoadingOverlay"
          ></LoadingOverlay>
        </CardBody>
      </CardStandard>
    );
  });
